import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from '@tanstack/react-router';
import { Badge, Button, TextLink, cn } from '@wit-support/ui';
import { Check, CircleAlert, Plus } from 'lucide-react';
/**
 * Чего не хватает профилю — подписями полей реестра («Instagram»), а не
 * ключами (`account.instagramUserId`).
 */
export function missingText(missing, byKey) {
    return missing.map((k) => byKey?.get(k)?.label ?? k).join(', ');
}
/**
 * Почему кабинет не готов к запуску; `null` — готов. Готовность считает
 * admin-api: `missing = [] ∧ profile.enabled ∧ platformEnabled` — здесь только
 * причина словами, в порядке «что чинить первым».
 */
export function notReadyText(a, byKey) {
    if (a.readiness.ok)
        return null;
    if (a.profile == null)
        return 'профиль не заполнен';
    if (a.readiness.missing.length > 0) {
        return `не заполнено: ${missingText(a.readiness.missing, byKey)}`;
    }
    if (!a.profile.enabled)
        return 'выключен в конструкторе';
    if (!a.platformEnabled)
        return 'выключен в платформе';
    return 'не готов';
}
/** Подпись чипа кабинета: «Linda · USD». */
export function accountChipLabel(a) {
    return a.currency ? `${a.name} · ${a.currency}` : a.name;
}
/**
 * Кабинеты пачки — карточки-переключатели. Выбранная — рамка цвета бренда,
 * лёгкая подложка и ✓ (не сплошная заливка); невыбранная — обычная рамка и
 * «+»; неготовый профиль — ⚠ и «не заполнено: Instagram» со ссылкой
 * «Заполнить профиль». Карточки не тянутся по высоте соседа (`items-start`).
 * Неготовый кабинет выбрать можно (видно, что мешает), но «Проверить» тогда
 * закрыт.
 */
export function AccountChips({ accounts, selected, onChange, byKey, allowed, disabled, }) {
    const list = accounts.filter((a) => a.platformEnabled &&
        (!allowed || allowed.length === 0 || allowed.includes(a.platformAccountId)));
    if (list.length === 0) {
        return (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u041D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u044B\u0445 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u043E\u0432. \u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043F\u0440\u043E\u0444\u0438\u043B\u044C \u0432 \u0440\u0430\u0437\u0434\u0435\u043B\u0435 \u00AB\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B\u00BB." }));
    }
    const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    return (_jsx("div", { className: "flex flex-wrap items-start gap-2", "data-testid": "account-chips", children: list.map((a) => {
            const on = selected.includes(a.platformAccountId);
            const notReady = notReadyText(a, byKey);
            return (_jsxs("div", { "data-selected": on ? 'true' : 'false', className: cn('flex min-w-40 max-w-full flex-col gap-1 rounded-[var(--radius-2)] border p-1 transition-colors', on
                    ? 'border-[var(--color-brand-bg)] bg-[color-mix(in_srgb,var(--color-brand-bg)_8%,transparent)]'
                    : 'border-[var(--color-border-default)]'), children: [_jsxs(Button, { type: "button", size: "sm", variant: "ghost", "aria-pressed": on, disabled: disabled, className: "justify-start gap-1.5 hover:bg-transparent", onClick: () => toggle(a.platformAccountId), children: [on ? (_jsx(Check, { "aria-hidden": true, className: "h-4 w-4 shrink-0 text-[var(--color-brand-bg)]", "data-testid": "chip-check" })) : notReady !== null ? (_jsx(CircleAlert, { "aria-hidden": true, className: "h-4 w-4 shrink-0 text-[var(--color-state-warning-fg)]" })) : (_jsx(Plus, { "aria-hidden": true, className: "h-4 w-4 shrink-0 text-[var(--color-text-muted)]" })), _jsx("span", { className: "truncate", children: accountChipLabel(a) })] }), notReady !== null ? (_jsxs("div", { className: "flex flex-col items-start gap-0.5 px-2 pb-1 text-xs", children: [_jsx(Badge, { variant: "warning", children: notReady }), _jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/fb-launches/accounts/$id", params: { id: a.platformAccountId }, children: "\u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u044C \u043F\u0440\u043E\u0444\u0438\u043B\u044C" }) })] })) : null] }, a.platformAccountId));
        }) }));
}
/** Все выбранные кабинеты готовы к запуску. */
/**
 * Почему проверка пачки недоступна — по первому неготовому выбранному
 * кабинету — законченная фраза: «кабинет «Soulmate»: выключен в
 * конструкторе». null — все готовы.
 */
export function firstNotReadyText(accounts, selected, byKey) {
    for (const id of selected) {
        const a = accounts.find((x) => x.platformAccountId === id);
        if (a == null)
            return 'одного из выбранных кабинетов больше нет в списке';
        const why = notReadyText(a, byKey);
        if (why != null)
            return `кабинет «${a.name}»: ${why}`;
    }
    return null;
}
export function allReady(accounts, selected) {
    return selected.every((id) => {
        const a = accounts.find((x) => x.platformAccountId === id);
        return a != null && a.readiness.ok;
    });
}
//# sourceMappingURL=account-chips.js.map